import React, { useState, useMemo, useEffect, useRef } from 'react'
import { IoArrowBack, IoReload } from "react-icons/io5";
import { Tooltip, Drawer, Spin, notification, Switch, Input } from 'antd';
import { Box, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { ceil, cloneDeep, findIndex, debounce, trim } from 'lodash';
import { BiSolidEdit } from "react-icons/bi";

import ProductImg from "../../../assets/images/newImages/nestle.svg";

import Pagination from "../../../components/pagination/index";
import SearchInput from '../../../components/uiComponent/inputs/searchinput/index';
import Button from "../../../components/uiComponent/button/index";
import AgGrid from "../../../components/ag-grid-table/index";
import WorkRowDetails from './workRowDetails.js';

import { PoDetailsWrapper } from '../../purchase-order/po-details/style.js';
import { AddSkuDrawerWrapper } from './style'

import {
  GetRemainingSkuList,
  GetWorkOrderDetails,
  GetGatedStatus,
  updateShipQty,
  AddOrderItems,
  GetOrderItems,
  SyncSkuAmazonFlags,
  setUpdatedSkuStatus,
} from '../../../slices/work-orders';

const defaultColDef = {
  resizable: true,
  sortable: false,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
  suppressMovable: true,
};

const AddSkuDrawer = ({ addSku, setAddSku, workOrderId, onAdd }) => {
  const dispatch = useDispatch();
  const searchRef = useRef(null);
  const gridApiRef = useRef(null);
  const gridColumnApiRef = useRef(null);

  const [page, setPage] = useState({ pageSize: 25, pageNumber: 1 });
  const [sortBy, setSortBy] = useState({ key: '', type: '' });
  const [filters, setFilters] = useState({ search: '' });
  const [orderItems, setOrderItems] = useState([]);
  const [skuRowIndexes, setSkuRowIndexes] = useState([]);
  const [selectedOrderItems, setSelectedOrderItems] = useState([])
  const [isViewSelected, setIsViewSelected] = useState(false);

  const [editWorkOrderItem, setEditWorkOrderItem] = useState({ id: '', key: '', value: '' });
  const [newValue, setNewValue] = useState('');

  const {
    fetchingRemainingSkuList,
    remainingSkus,
    remainingSkusCount,
    addingOrderItems,
    fetchingSkuGatedStatus,
  } = useSelector((state) => state.workOrders || {});

  useEffect(() => {
    dispatch(GetRemainingSkuList({
      workOrderId,
      page,
      filters,
      sortBy,
      selectedOrderItems
    }));
  }, [isViewSelected]);

  const gridReady = (params) => {
    gridApiRef.current = params.api;
    gridColumnApiRef.current = params.columnApi;
  };

  const modelChange = (params) => {
    params.api.forEachNode((node) => {
      const matchedItem = selectedOrderItems.find(item => item?.id === node?.data?.id);

      if (matchedItem) {
        const shipQtyDifference = matchedItem.ship_quantity - (node.data.ship_quantity || 0);

        const updatedProductsV2 = (matchedItem.products_v2 || []).map((product) => {
          const bundle_quantity = product?.product_sku_mappings_v2?.bundle_quantity || 1;
          const reservedQty = product?.reserved_qty || 0;
          const warehouseStock = product?.warehouse_stock || 0;

          return {
            ...product,
            reserved_qty: reservedQty + (bundle_quantity * shipQtyDifference),
            warehouse_stock: warehouseStock - (bundle_quantity * shipQtyDifference),
          };
        });

        node.setSelected(true);

        node.setData({
          ...node.data,
          ship_quantity: matchedItem.ship_quantity,
          reserved_qty: matchedItem.reserved_qty,
          warehouse_stock: matchedItem.warehouse_stock,
          products_v2: updatedProductsV2,
        });

      }
    });
  };

  const handleGetSkuGatedStatus = ({ skuId, index }) => {
    setSkuRowIndexes(skuRowIndexes => [...skuRowIndexes, index]);
    dispatch(GetGatedStatus({ skuId, workOrderId }))
      .then(({ payload }) => {
        if (payload?.success) {
          dispatch(setUpdatedSkuStatus({
            field: 'remainingSkus',
            index,
            value: payload?.skuStatus
          }));
        }
        setSkuRowIndexes(skuRowIndexes => skuRowIndexes.filter((rowIndex) => rowIndex !== index));
      });
  };

  const handleUpdateShipQty = (params) => {
    const { newValue, oldValue = 0, data } = params || {};
    const { id, warehouse_stock = 0 } = data || {};

    if (!newValue || isNaN(newValue) || (newValue < 0) || (String(newValue).trim() === '') || (newValue === oldValue)) {
      notification.warning({
        message: 'Please enter a valid ship quantity!',
        top: 60
      });
      return false;
    }


    setOrderItems(orderItems => {
      const index = findIndex(orderItems, (item) => item.sku_id === id);

      if (index === -1) {
        const updatedOrderItems = [...orderItems, { sku_id: id, ship_quantity: newValue }];
        return updatedOrderItems;
      }
      else {
        const orderItemsClone = cloneDeep(orderItems);
        orderItemsClone[index].ship_quantity = newValue;
        return orderItemsClone;
      }
    });

    const remainingSkusIndex = findIndex(remainingSkus, (item) => item.id === id);

    if (remainingSkus[remainingSkusIndex]) {
      dispatch(updateShipQty({
        field: 'remainingSkus',
        index: remainingSkusIndex,
        value: newValue
      }));
    }

    const index = remainingSkus.findIndex(sku => sku?.id === data?.id)

    setSelectedOrderItems(prevItems => {
      const previousIndex = prevItems.findIndex(item => item?.id === data?.id);
      const previousItem = prevItems[previousIndex] || {};


      const baseItem = cloneDeep(prevItems[previousIndex] || selectedOrderItems[index]);

      if (!baseItem) return prevItems;

      baseItem.ship_quantity = newValue;

      const updated = [...prevItems];
      updated[previousIndex] = baseItem;
      return updated;
    });


    onSelectionChange();

    return true;
    return true;
  };

  const handleSave = (id, key, value, oldValue, data) => {
    if (!id || !key) return;

    if (key === 'ship_quantity') {
      const parsedValue = parseInt(value, 10);
      if (isNaN(parsedValue) || parsedValue < 0 || String(value).trim() === '') {
        setEditWorkOrderItem({ id: '', key: '', value: '' });
        return notification.warning({
          message: 'Please enter a valid ship quantity!',
          top: 60
        });
      }

      if (parsedValue === parseInt(oldValue, 10)) {
        setEditWorkOrderItem({ id: '', key: '', value: '' });
        return;
      }

      handleUpdateShipQty({ newValue: parsedValue, oldValue, data });
      setEditWorkOrderItem({ id: '', key: '', value: '' });
    }
  };

  const handleKeyDown = (e, data) => {
    e.stopPropagation();

    if (e.key === 'Enter') {
      const { value } = e.target || {};
      const { id, key, value: oldValue } = editWorkOrderItem || {};
      handleSave(id, key, value, oldValue, data);
    }
  };

  const columnDefs = useMemo(() => [
    {
      headerName: "",
      children: [
        { headerName: '', field: "products_v2.length", cellRenderer: "agGroupCellRenderer", width: 75, }
      ]
    },
    {
      children: [{
        headerCheckboxSelection: true,
        width: 35,
        checkboxSelection: true,
        sortable: false,
      }],
    },
    {
      headerName: 'Product Name',
      children: [
        {
          headerName: '',
          headerClass: 'parent-header',
          minWidth: 214,
          flex: 1,
          cellRenderer: ({ data }) => {
            const { item_name, image_url } = data || {};

            return (
              <div className='card-wrapper'>
                <img style={{ width: '50px', height: '50px', objectFit: 'contain', maxWidth: '50px' }}
                  src={image_url || ProductImg}
                  alt="ProductImg"
                  title="ProductImg"
                />
                <span className='two-line-ellipsis'>
                  {item_name || '---'}
                </span>
              </div>
            )
          }
        }
      ]
    },
    {
      headerName: 'SKU/ASIN',
      children: [
        {
          headerName: '',
          headerClass: 'parent-header',
          minWidth: 180,
          flex: 1,
          cellRenderer: ({ data }) => {
            const { sku, vendorasin } = data || {};

            return (
              <Box display="flex" flexDirection="column">
                <Typography component='span' fontSize='14px' className='upc-code'><Typography component='span' fontSize='14px' color="#7E8A90">SKU:</Typography> {sku || '---'}</Typography>
                <Typography component='span' fontSize='14px' className='upc-code'><Typography component='span' fontSize='14px' color="#7E8A90">ASIN:</Typography> {vendorasin?.asin || '---'}</Typography>
              </Box>
            )
          }
        }
      ]
    },
    // {
    //   headerName: 'Bundle Qty',
    //   children: [
    //     {
    //       headerName: '',
    //       headerClass: 'parent-header',
    //       sortable: true,
    //       comparator: () => 0,
    //       minWidth: 90,
    //       flex: 1,
    //       field: 'warehouse_stock',
    //       cellRenderer: ({ value }) => {
    //         return (
    //           <div className='card-wrapper gap-1'>
    //             <span>{value || 0}</span>
    //           </div>
    //         )
    //       },
    //     }
    //   ]
    // },
    {
      headerName: 'Amazon Stock',
      headerClass: 'parent-header-stock',
      children: [
        {
          headerName: 'Total',
          sortable: true,
          comparator: () => 0,
          headerClass: 'amazon-parent-header',
          minWidth: 60,
          flex: 1,
          field: 'amazon_total',
          cellRenderer: ({ value }) => {
            return (
              <div className='edit-able-data'>
                {/* <Popover
                  title={null}
                  content={<InfoTooltipData />}
                  placement='right'>
                </Popover> */}
                <span>{value || 0}</span>
              </div>
            )
          },
        },
        {
          headerName: 'Available',
          sortable: true,
          comparator: () => 0,
          headerClass: 'amazon-parent-header',
          minWidth: 75,
          flex: 1,
          field: 'amazon_available',
          cellRenderer: ({ value }) => {
            return (
              <div className='edit-able-data'>
                <span>{value || 0}</span>
              </div>
            )
          },
        }
      ]
    },
    {
      headerName: 'WH Stock',
      children: [
        {
          headerName: '',
          headerClass: 'parent-header',
          sortable: true,
          comparator: () => 0,
          minWidth: 75,
          flex: 1,
          field: 'warehouse_stock',
          cellRenderer: ({ value }) => {
            return (
              <div>{value || 0}</div>
            )
          }
        }
      ]
    },
    // {
    //   headerName: 'Reserved Quantity',
    //   children: [
    //     {
    //       headerName: '',
    //       headerClass: 'parent-header',
    //       sortable: true,
    //       comparator: () => 0,
    //       minWidth: 120,
    //       field: 'reserved_qty',
    //       flex: 1,
    //       cellRenderer: ({ value }) => {
    //         return (
    //           <div>{value || 0}</div>
    //         )
    //       }
    //     }
    //   ]
    // },
    {
      headerName: 'Ship Quantity',
      children: [
        {
          headerName: '',
          headerClass: 'parent-header',
          minWidth: 110,
          flex: 1,
          field: 'ship_quantity',
          valueSetter: handleUpdateShipQty,
          cellRenderer: ({ data }) => {
            const id = data?.id;
            const shipQuantity = data?.ship_quantity || 0;
            const showInput = (editWorkOrderItem?.id === id) && (editWorkOrderItem?.key === 'ship_quantity');

            return (
              <div className='edit-able-data'>
                {
                  showInput ? <Input
                    autoFocus={true}
                    defaultValue={newValue}
                    onKeyDown={(e) => handleKeyDown(e, data)}
                    onBlur={(e) => handleSave(id, 'ship_quantity', e.target.value, shipQuantity, data)}
                    size="small"
                  /> :
                  <span>{shipQuantity || 0}</span>
                }
                <BiSolidEdit 
                  className={showInput ? 'increase-font' : ''} 
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditWorkOrderItem({ id, key: 'ship_quantity', value: shipQuantity });
                    setNewValue(String(shipQuantity));
                  }} 
                />
              </div>
            );
          }
        }
      ]
    },
    {
      headerName: 'Gated Status',
      children: [
        {
          headerName: '',
          headerClass: 'parent-header',
          minWidth: 130,
          sortable: true,
          field: 'sku_status',
          flex: 1,
          comparator: () => '',
          cellRenderer: ({ data }) => {
            const { sku_status, id } = data || {};
            const index = findIndex(remainingSkus, (item) => item.id === id);

            return (
              <div className='not-gated-icon-wrapper'>
                <span className={`bag-icon-gated ${sku_status === 'Gated' ? 'new-gated' : ''}`}>
                  {
                    (skuRowIndexes.includes(index)) ? 'fetching...' : sku_status?.toLowerCase() === 'non-gated' ? 'Not Gated' : sku_status || '---'
                  }
                </span>
                <IoReload
                  onClick={() => handleGetSkuGatedStatus({ skuId: id, index })}
                  style={skuRowIndexes.includes(index) ? { pointerEvents: 'none' } : {}}
                />
              </div>
            )
          }
        }
      ]
    }
  ], [remainingSkus, skuRowIndexes, editWorkOrderItem, newValue]);

  const InfoTooltipData = () => {
    return (
      <PoDetailsWrapper className='mt-0'>
        <div className='container-box'>
          <div className='items'>
            <span>On Confirmed POs</span>
            <span>0</span>
          </div>
          <div className='items'>
            <span>Warehouse Stock</span>
            <span>0</span>
          </div>
        </div>
      </PoDetailsWrapper>
    )
  }

  const handleSearch = debounce((value) => {
    if (trim(value) || filters.search) {
      setFilters(filters => ({ ...filters, search: value }));
      setPage(page => ({ ...page, pageNumber: 1 }));
      dispatch(GetRemainingSkuList({
        workOrderId,
        page: { ...page, pageNumber: 1 },
        filters: { ...filters, search: value },
        sortBy,
        selectedOrderItems
      }));
    }
  }, 800);

  const handlePagination = (number, key) => {
    const value = Number(number);
    let newPage = {};

    if (key === 'pageSize') newPage = { pageNumber: 1, pageSize: value };
    else newPage = { ...page, pageNumber: value };

    setPage(newPage);
    dispatch(GetRemainingSkuList({
      page: newPage,
      workOrderId,
      sortBy,
      filters,
      selectedOrderItems
    }));
  };

  const refreshGridClick = () => {
    if (gridColumnApiRef.current){
      gridColumnApiRef.current.applyColumnState({
        defaultState: { sort: null },
        applyOrder: true,
      });
      setSortBy({ key: '', type: '' })
    }
    dispatch(GetRemainingSkuList({
      page,
      workOrderId,
      filters,
      selectedOrderItems
    }));
    setOrderItems([]);
  };

  const onSelectionChange = (e) => {
    if (!e) {
      setOrderItems(orderItems => {
        orderItems?.forEach(({ sku_id }) => {
          const node = gridApiRef.current?.getRowNode(sku_id);
          if (node) node.setSelected(true);
        });
        return orderItems;
      });
    }
    else {
      const selectedRows = gridApiRef.current?.getSelectedRows() || [];
      const data = selectedRows?.map(({ id, ship_quantity = 0 }) => ({ sku_id: id, ship_quantity }));
      setOrderItems(data);
      const selectedItems = remainingSkus.filter(sku => data.some(item => item?.sku_id === sku?.id))
      setSelectedOrderItems((prevState) => {
        const newItems = selectedItems.filter(
          (item) => !prevState.some((prev) => prev?.id === item?.id)
        );

        const updatedItems = newItems.map(item => ({
          ...item,
          sku_id: item.id,
          ship_quantity: item.ship_quantity || 0,
        }));
        return [...prevState, ...updatedItems];
      });
    }
  };

  const onRowSelected = (event) => {
    const { data, node } = event;

    if (!node.isSelected() && (isViewSelected ? true : remainingSkus.some(sku => sku?.id === data?.id))) {
      setSelectedOrderItems(prev => prev.filter(item => item?.id !== data?.id));
    }
  };

  const getBtnDisableTooltip = () => {
    if (fetchingSkuGatedStatus) return 'Fetching Sku Gated Status';
    if (!selectedOrderItems?.length) return 'Please select at least 1 sku';
    if (selectedOrderItems?.some(item =>
      remainingSkus?.some(sku =>
        (sku.id === item.sku_id) && (sku.sku_status === 'Gated')
      )
    )) {
      return 'One or more selected SKUs are GATED';
    }
    if (selectedOrderItems?.some(item => item.ship_quantity == 0)) return 'The Ship Quantity for one or more selected SKUs is zero';

    return '';
  };

  const handleAddToWorkOrder = () => {

    const resultArray = selectedOrderItems.map(({ id, ship_quantity }) => ({
      sku_id: id,
      ship_quantity: ship_quantity || 0,
    }));

    if (onAdd) {
      onAdd(selectedOrderItems);
      setAddSku(false);
      return;
    }

    dispatch(AddOrderItems({
      workOrderId,
      orderItems: resultArray
    }))
      .then(({ payload }) => {
        if (payload?.success) {
          setAddSku(false);
          // Show the new rows immediately — don't make the grid wait on Amazon.
          dispatch(GetOrderItems({
            workOrderId
          })).then(() => {
            dispatch(GetWorkOrderDetails(workOrderId));
          });

          // Then resolve Amazon's label owner + expiry-required flag for those SKUs in the
          // background (only NULL values are fetched, so re-adding known SKUs is a no-op) and
          // refresh once more if anything was actually learned. `syncingSkuFlags` drives the
          // page loader so the wait is visible rather than a silent stale grid.
          dispatch(SyncSkuAmazonFlags({ skuIds: resultArray.map(({ sku_id }) => sku_id) }))
            .then(({ payload: syncPayload }) => {
              if (syncPayload?.data?.updated > 0) {
                dispatch(GetOrderItems({ workOrderId }));
              }
            });
        }
      });
  };

  const onSortChange = (params) => {
    const sortState = params.columnApi.getColumnState().find(col => col.sort != null);
    if (!sortState) return;

    const { colId, sort } = sortState;
    if (isViewSelected) {
      const sortKey = (colId === 'warehouse_stock_1') ? 'warehouse_stock' : colId;
      const sortedData = [...selectedOrderItems].sort((a, b) => {
        const aValue = parseInt(a[sortKey]) || 0;
        const bValue = parseInt(b[sortKey]) || 0;

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sort === 'asc' ? -1 : 1;
        if (bValue == null) return sort === 'asc' ? 1 : -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sort === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return sort === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }
      });

      setSelectedOrderItems(sortedData);
    }
    else {
      if (sortState && ['warehouse_stock', 'warehouse_stock_1', 'amazon_total', 'amazon_available', 'reserved_qty', 'sku_status'].includes(sortState?.colId)) {
        const { colId, sort } = sortState || {};
        setSortBy({ key: (colId === 'warehouse_stock_1') ? 'warehouse_stock' : colId, type: sort });

        dispatch(GetRemainingSkuList({
          page,
          workOrderId,
          sortBy: { key: (colId === 'warehouse_stock_1') ? 'warehouse_stock' : colId, type: sort },
          filters,
          selectedOrderItems
        }));
      }
    }

  };

  const { pageNumber, pageSize } = page || {};

  return (
    <Drawer visible={addSku} closable={false} onClose={() => setAddSku(false)} width={'calc(100vw - 260px)'} bodyStyle={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AddSkuDrawerWrapper>
        <header>
          <div onClick={() => setAddSku(false)} className='back-icon'>
            <IoArrowBack />
            <span>Add SKU</span>
          </div>
          <div className='side-actions'>
            <div className='switch-tab-wrapper'>
              <span className='switch-tab'>view selected</span>
              <span className='switch-count'>{selectedOrderItems.length}</span>
              <Switch
                size='small'
                onChange={(checked) => {
                  setIsViewSelected(checked);
                  setFilters({ search: '' })
                  if (searchRef.current) {
                    searchRef.current.value = '';
                  }
                }} />
            </div>
            <SearchInput
              inputRef={searchRef}
              disabled={isViewSelected}
              className="search"
              placeholder="Search by Title, ASIN, SKU, Identifiers"
              onChange={({ target }) => handleSearch(target.value)}
            />
          </div>
        </header>
        <div className="drawer-body">
          <Spin className='add-sku-drawer-loading' tip='Loading...' spinning={fetchingRemainingSkuList || addingOrderItems}>
            <AgGrid
              user_referrals={isViewSelected ? selectedOrderItems : remainingSkus}
              defaultColDef={defaultColDef}
              columnDefinitions={columnDefs}
              rowHeight={64}
              headerHeight={36}
              height="calc(100vh - 220px)"
              containerHeight="calc(100vh - 220px)"
              rowSelection='multiple'
              masterDetail={true}
              detailRowAutoHeight={true}
              gridReady={gridReady}
              onSortChange={onSortChange}
              onSelectionChanged={onSelectionChange}
              detailCellRenderer={(props) => <WorkRowDetails {...props} />}
              className="sku-table"
              getRowId={(params) => params.data.id}
              onModelUpdated={modelChange}
              onRowSelected={onRowSelected}
            />
            {
              !isViewSelected &&
              (
                <Pagination
                  total={remainingSkusCount}
                  totalPages={ceil(remainingSkusCount / pageSize + 1)}
                  pageNumber={pageNumber}
                  pageLimit={pageSize}
                  handleLimitChange={(e) => handlePagination(e, 'pageSize')}
                  handlePageNumberChange={(e) => handlePagination(e, 'pageNumber')}
                  handlePageChangeLeft={(e) => handlePagination(e, 'pageNumber')}
                  handlePageChangeRight={(e) => handlePagination(e, 'pageNumber')}
                  refreshGridClick={refreshGridClick}
                  loading={fetchingSkuGatedStatus}
                />
              )
            }

          </Spin>
        </div>
        <Box className="drawer-footer" pb={0} justifyContent="end" display="flex" alignItems="center" gap={2}>
          <Button
            text="Cancel"
            className="outlined mb-0"
            minWidth="90px"
            onClick={() => setAddSku(false)}
          />
          <Tooltip placement='top' title={getBtnDisableTooltip()}>
            <div style={{ display: "inline-block" }}>
              <Button
                text="Add to Work Order"
                className="primary mb-0"
                minWidth="90px"
                onClick={handleAddToWorkOrder}
                disabled={getBtnDisableTooltip()}
              />
            </div>
          </Tooltip>
        </Box>
      </AddSkuDrawerWrapper>
    </Drawer>
  )
}

export default AddSkuDrawer